import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Download, Pencil, Plus, RefreshCw, ArrowRightLeft, Truck, X, History, PiggyBank, Landmark, Smartphone, Wallet, ArrowDownToLine, ArrowUpFromLine, Settings2 } from 'lucide-react';
import { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Toast from '../../components/admin/Toast';
import ReceivablesPage from './ReceivablesPage';
import PayablesPage from './PayablesPage';
import ExpensesPage from './ExpensesPage';
import JournalEntriesPage from './JournalEntriesPage';
import './ERPManagement.css';
import './Accounts.css';

type AccountPage = 'overview' | 'payables' | 'receivables' | 'transactions' | 'ledger' | 'cash-bank' | 'profit-loss' | 'cash-sessions' | 'cod-settlements' | 'expenses' | 'journal-entries' | 'financial-reports' | 'settings';

const titles: Record<AccountPage, string> = {
  overview: 'Dashboard',
  payables: 'Payables',
  receivables: 'Receivables',
  transactions: 'Transactions',
  ledger: 'General Ledger',
  'cash-bank': 'Cash & Banks',
  'profit-loss': 'Profit & Loss',
  'cash-sessions': 'Cash Sessions',
  'cod-settlements': 'Courier Settlements',
  expenses: 'Expenses',
  'journal-entries': 'Journal Entries',
  'financial-reports': 'Financial Reports',
  settings: 'Accounting Settings',
};

const rows = (payload: any): any[] => Array.isArray(payload) ? payload : (payload?.results || []);
const money = (value: any) => `\u09F3${Number(value || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (value: any) => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const shortDate = (value: any) => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';
const errorText = (error: any) => error?.response?.data?.detail || error?.message || 'Request failed';

const AccountsManagement: React.FC<{ page: AccountPage }> = ({ page }) => {
  const [payload, setPayload] = useState<any>(null);
  const [me, setMe] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [cashBankAccounts, setCashBankAccounts] = useState<any[]>([]);
  const [receivables, setReceivables] = useState<any[]>([]);
  const [shippingProviders, setShippingProviders] = useState<any[]>([]);
  const [coaList, setCoaList] = useState<any[]>([]);
  const [settingsTab, setSettingsTab] = useState('payment-accounts');
  const [catForm, setCatForm] = useState<Record<string, any>>({});
  const [pmtForm, setPmtForm] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [locationFilter, setLocationFilter] = useState(() => sessionStorage.getItem('erp-location-filter') || '');
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [form, setForm] = useState<Record<string, any>>({});
  const [modal, setModal] = useState<'cash-in' | 'cash-out' | 'transfer' | 'history' | 'add-account' | 'edit-account' | 'transaction-history' | null>(null);
  const [expandedLocs, setExpandedLocs] = useState<Record<string, boolean>>({});
  const [cashTab, setCashTab] = useState<'accounts' | 'cash-flow' | 'ledger' | 'transfers' | 'sessions' | 'settlements'>('accounts');
  const [reportTab, setReportTab] = useState('Profit & Loss');
  const [frData, setFrData] = useState<any>(null);
  const [frLoading, setFrLoading] = useState(false);
  const [frDates, setFrDates] = useState<Record<string, string>>({});

  const can = useCallback((code: string) => Boolean(me?.is_superuser || me?.is_admin || (me?.permissions || []).includes(code)), [me]);
  const params = useMemo(() => ({ ...filters, ...(locationFilter ? { location: locationFilter } : {}) }), [filters, locationFilter]);

  const loadReport = useCallback(async (tab: string) => {
    setFrLoading(true);
    setFrData(null);
    const endpoint = tab === 'Profit & Loss' ? 'accounts-section/profit-loss' :
      tab === 'Trial Balance' ? 'reports/trial-balance' :
      tab === 'Balance Sheet' ? 'reports/balance-sheet' :
      tab === 'Cash Flow' ? 'reports/cash-flow' :
      tab === 'AR Aging' ? 'reports/ar-aging' :
      tab === 'AP Aging' ? 'reports/ap-aging' : '';
    if (!endpoint) return setFrLoading(false);
    const p = { ...params, ...(frDates.start ? { start: frDates.start } : {}), ...(frDates.end ? { end: frDates.end } : {}) };
    try {
      const resp = await (endpoint.startsWith('reports/') ? erpAPI.report(endpoint.replace('reports/', ''), p) : erpAPI.list(endpoint, p));
      setFrData(resp.data);
    } catch { setFrData(null); }
    finally { setFrLoading(false); }
  }, [params, frDates]);

  useEffect(() => { if (page === 'financial-reports') loadReport(reportTab); }, [reportTab, loadReport, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [identity, locs] = await Promise.all([
        erpAPI.me(),
        erpAPI.list('locations'),
      ]);
      setMe(identity.data);
      setLocations(rows(locs.data));

      if (page === 'overview') {
        const [resp, accts] = await Promise.all([
          erpAPI.list(`accounts-section/overview`, params),
          erpAPI.list('accounts-section/cash-bank', locationFilter ? { location: locationFilter } : undefined).catch(() => ({ data: { accounts: [] } })),
        ]);
        setPayload(resp.data);
        setAccounts(accts.data?.accounts || []);
      } else if (page === 'cash-bank') {
        const [resp, transf, coa] = await Promise.all([
          erpAPI.list('accounts-section/cash-bank', params),
          erpAPI.list('accounts-section/cash-transfers', params).catch(() => ({ data: [] })),
          erpAPI.list('accounts', { account_type: 'ASSET' }).catch(() => ({ data: { results: [] } })),
        ]);
        setPayload({ ...resp.data, transfers: Array.isArray(transf.data) ? transf.data : [] });
        setCashBankAccounts(resp.data?.accounts || []);
        setCoaList(Array.isArray(coa.data) ? coa.data : (coa.data?.results || []));
      } else if (page === 'cash-sessions') {
        const [resp, accts] = await Promise.all([
          erpAPI.list('accounts-section/sessions', params),
          erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } })),
        ]);
        setPayload(resp.data);
        setCashBankAccounts(accts.data?.accounts || []);
      } else if (page === 'cod-settlements') {
        const [resp, accts, recvResp, provResp] = await Promise.all([
          erpAPI.list('accounts-section/cod-settlements', params),
          erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } })),
          erpAPI.list('website-order-receivables', { status: 'UNPAID' }).catch(() => ({ data: [] })),
          erpAPI.list('accounts-section/shipping-providers').catch(() => ({ data: [] })),
        ]);
        setPayload(resp.data);
        setCashBankAccounts(accts.data?.accounts || []);
        setReceivables(Array.isArray(recvResp.data) ? recvResp.data : recvResp.data?.results || []);
        setShippingProviders(Array.isArray(provResp.data) ? provResp.data : []);
      } else if (page === 'receivables') {
        const resp = await erpAPI.list('accounts-section/receivables', params);
        setPayload(resp.data);
        const accts = await erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } }));
        setCashBankAccounts(accts.data?.accounts || []);
      } else if (['expenses', 'journal-entries', 'financial-reports'].includes(page)) {
        setPayload({});
      } else if (page === 'settings') {
        const [pmts, cats, banks] = await Promise.all([
          erpAPI.list('payment-accounts', {}).catch(() => ({ data: [] })),
          erpAPI.list('expense-categories', {}).catch(() => ({ data: [] })),
          erpAPI.list('accounts-section/cash-bank', {}).catch(() => ({ data: { accounts: [] } })),
        ]);
        setPayload({ paymentMethods: rows(pmts.data), expenseCategories: rows(cats.data) });
        setCashBankAccounts(banks.data?.accounts || []);
      } else {
        const resp = await erpAPI.list(`accounts-section/${page}`, params);
        setPayload(resp.data);
      }
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    } finally {
      setLoading(false);
    }
  }, [page, params, locationFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const change = (event: Event) => {
      const next = String((event as CustomEvent).detail?.location || '');
      sessionStorage.setItem('erp-location-filter', next);
      setLocationFilter(next);
    };
    window.addEventListener('erp-location-change', change);
    return () => window.removeEventListener('erp-location-change', change);
  }, []);

  const saveMovement = async (movement_type: string) => {
    try {
      await erpAPI.create('accounts-section/cash-bank/move', {
        location: Number(form.location || locationFilter || locations[0]?.id),
        account: Number(form.account), to_account: form.to_account ? Number(form.to_account) : null,
        amount: Number(form.amount), movement_type, note: form.note || '',
      });
      setForm({}); setModal(null); setMessage({ type: 'success', text: 'Movement recorded.' }); await load();
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    }
  };

  const createTransfer = async () => {
    try {
      await erpAPI.create('accounts-section/cash-transfers', {
        source_account: Number(form.source_account), destination_account: Number(form.destination_account),
        location: Number(form.location || locationFilter || locations[0]?.id),
        amount: Number(form.amount), notes: form.notes || '', reference: form.reference || '',
      });
      setForm({}); setModal(null); setMessage({ type: 'success', text: 'Transfer created.' }); await load();
    } catch (error) {
      setMessage({ type: 'error', text: errorText(error) });
    }
  };

  const approveTransfer = async (id: number) => {
    try {
      await erpAPI.action('accounts-section', id, 'cash-transfers/approve', {});
      setMessage({ type: 'success', text: 'Transfer approved.' }); setModal(null); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const completeTransfer = async (id: number) => {
    try {
      await erpAPI.action('accounts-section', id, 'cash-transfers/complete', {});
      setMessage({ type: 'success', text: 'Transfer completed.' }); setModal(null); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const cancelTransfer = async (id: number) => {
    try {
      await erpAPI.action('accounts-section', id, 'cash-transfers/cancel', {});
      setMessage({ type: 'success', text: 'Transfer cancelled.' }); setModal(null); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const openSession = async () => {
    try {
      await erpAPI.create('accounts-section/sessions', {
        location: Number(form.location || locationFilter || locations[0]?.id),
        cash_account: Number(form.cash_account), opening_float: Number(form.opening_float || 0), notes: form.notes || '',
      });
      setForm({}); setMessage({ type: 'success', text: 'Session opened.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const closeSession = async (id: number) => {
    try {
      await erpAPI.action('accounts-section', id, 'sessions/close', {
        counted_cash: Number(form[`counted_${id}`] || 0), notes: form[`close_notes_${id}`] || '',
      });
      setForm({}); setMessage({ type: 'success', text: 'Session closed.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const reconcileSession = async (id: number) => {
    try {
      await erpAPI.action('accounts-section', id, 'sessions/reconcile', {});
      setMessage({ type: 'success', text: 'Session reconciled.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const createCODSettlement = async () => {
    const raw = form.settlement_order_ids;
    const orderIds: number[] = Array.isArray(raw)
      ? raw.map((n: any) => Number(n)).filter((n: number) => n > 0)
      : raw ? String(raw).split(',').map((s: string) => Number(s.trim())).filter((n: number) => n > 0) : [];
    try {
      await erpAPI.create('accounts-section/cod-settlements', {
        location: Number(form.location || locationFilter || locations[0]?.id),
        cash_bank_account: Number(form.settlement_account),
        shipping_provider: form.settlement_provider ? Number(form.settlement_provider) : null,
        amount: Number(form.settlement_amount), settlement_date: form.settlement_date || undefined,
        reference_number: form.settlement_ref || '', notes: form.settlement_notes || '',
        order_ids: orderIds,
      });
      setForm({}); setMessage({ type: 'success', text: 'Settlement recorded.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const createCashBankAccount = async () => {
    try {
      await erpAPI.create('accounts-section/cash-bank/accounts', {
        location: form.add_location ? Number(form.add_location) : null,
        account: Number(form.add_account), name: form.add_name,
        opening_balance: Number(form.add_balance || 0),
      });
      setForm({}); setModal(null); setMessage({ type: 'success', text: 'Account created.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const updateCashBankAccount = async () => {
    try {
      await erpAPI.action('accounts-section', form.edit_id, 'update-cash-bank-account', {
        name: form.edit_name, account: Number(form.edit_account),
        account_number: form.edit_account_number || '', description: form.edit_description || '',
        is_active: form.edit_is_active !== false,
      });
      setForm({}); setModal(null); setMessage({ type: 'success', text: 'Account updated.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const deleteAccount = async (id: number) => {
    if (!window.confirm('Delete this account? This cannot be undone if the account has no transactions.')) return;
    try {
      const resp = await erpAPI.action('accounts-section', id, 'delete-cash-bank-account', {});
      setMessage({ type: 'success', text: resp.data?.detail || 'Deleted.' }); await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  if (loading && !payload) return <div className="erp-loading"><RefreshCw className="spin" /> Loading accounts...</div>;

  const coaAssetOptions = coaList.map((a: any) => ({ value: a.id, label: `${a.code} ${a.name}` }));

  const typeMeta = (t: string) => {
    const up = (t || '').toUpperCase();
    if (up === 'CASH') return { icon: <PiggyBank size={18} />, bg: '#ecfdf5', color: '#10b981', badge: 'acct-badge-cash', label: 'Cash on Hand' };
    if (up === 'BANK') return { icon: <Landmark size={18} />, bg: '#eff6ff', color: '#3b82f6', badge: 'acct-badge-bank', label: 'Bank Accounts' };
    if (up === 'BKASH' || up === 'NAGAD') return { icon: <Smartphone size={18} />, bg: '#f5f3ff', color: '#8b5cf6', badge: 'acct-badge-mobile', label: 'Mobile Wallets' };
    if (up === 'CARD') return { icon: <CreditCard size={18} />, bg: '#fefce8', color: '#f59e0b', badge: 'acct-badge-default', label: 'Card' };
    if (up === 'COD') return { icon: <Truck size={18} />, bg: '#fff7ed', color: '#f97316', badge: 'acct-badge-default', label: 'Courier Receivable' };
    return { icon: <Wallet size={18} />, bg: '#f1f5f9', color: '#64748b', badge: 'acct-badge-default', label: up };
  };

  const fieldGroup = (label: string, children: React.ReactNode) => (
    <div className="acct-form-group"><label className="acct-form-label">{label}</label>{children}</div>
  );

  const modalContent = (title: string, body: React.ReactNode, wide = false) => (
    <div className="acct-modal-overlay" onClick={() => setModal(null)}>
      <div className="acct-modal" style={{ maxWidth: wide ? 1080 : 480 }} onClick={e => e.stopPropagation()}>
        <div className="acct-modal-header">
          <h3 className="acct-modal-title">{title}</h3>
          <button className="acct-modal-close" onClick={() => setModal(null)}><X size={16} /></button>
        </div>
        <div className="acct-modal-body">{body}</div>
      </div>
    </div>
  );

  const modalForm = (fields: React.ReactNode, submitLabel: string, onSubmit: () => void, submitClass = 'acct-btn-primary') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {fields}
      <div className="acct-form-actions">
        <button className="acct-btn acct-btn-secondary" onClick={() => setModal(null)}>Cancel</button>
        <button className={`acct-btn ${submitClass}`} onClick={onSubmit}>{submitLabel}</button>
      </div>
    </div>
  );

  function TransactionHistoryModal({ account }: { account: any }) {
    const [txns, setTxns] = useState<any[]>([]);
    const [txLoading, setTxLoading] = useState(true);
    const [txError, setTxError] = useState<string | null>(null);
    const [txPage, setTxPage] = useState(1);
    const [txTotal, setTxTotal] = useState(0);
    const [txFilters, setTxFilters] = useState<Record<string, any>>({});
    const txPageSize = 20;
    const totalPages = Math.ceil(txTotal / txPageSize) || 1;

    useEffect(() => {
      setTxLoading(true);
      setTxError(null);
      erpAPI.list('accounts-section/transactions', {
        account_id: account?.id,
        page: txPage,
        page_size: txPageSize,
        ...(locationFilter ? { location: locationFilter } : {}),
        ...(txFilters.start ? { start: txFilters.start } : {}),
        ...(txFilters.end ? { end: txFilters.end } : {}),
        ...(txFilters.transaction_type ? { transaction_type: txFilters.transaction_type } : {}),
      })
        .then(resp => {
          setTxns(resp.data?.results || []);
          setTxTotal(resp.data?.count || 0);
        })
        .catch(err => setTxError(errorText(err)))
        .finally(() => setTxLoading(false));
    }, [account?.id, txPage, txFilters, locationFilter]);

    return modalContent(`${account?.account || 'Account'} — Transaction History`,
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="txn-history-filters">
          <div className="txn-history-filter-group">
            <span className="txn-history-filter-label">From</span>
            <input type="date" lang="en-GB" value={txFilters.start || ''} onChange={e => { setTxFilters(f => ({ ...f, start: e.target.value })); setTxPage(1); }} className="txn-history-filter-input" />
          </div>
          <div className="txn-history-filter-group">
            <span className="txn-history-filter-label">To</span>
            <input type="date" lang="en-GB" value={txFilters.end || ''} onChange={e => { setTxFilters(f => ({ ...f, end: e.target.value })); setTxPage(1); }} className="txn-history-filter-input" />
          </div>
          <div className="txn-history-filter-group">
            <span className="txn-history-filter-label">Type</span>
            <select className="txn-history-filter-input" value={txFilters.transaction_type || ''} onChange={e => { setTxFilters(f => ({ ...f, transaction_type: e.target.value })); setTxPage(1); }}>
              <option value="">All Types</option>
              <option value="CASH_IN">Cash In</option>
              <option value="CASH_OUT">Cash Out</option>
              <option value="TRANSFER">Transfer</option>
              <option value="RECEIPT">Receipt</option>
              <option value="BILL_PAYMENT">Bill Payment</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </select>
          </div>
          {(txFilters.start || txFilters.end || txFilters.transaction_type) && (
            <button className="acct-btn acct-btn-secondary" style={{ minHeight: 34, fontSize: '.85rem', alignSelf: 'flex-end' }} onClick={() => { setTxFilters({}); setTxPage(1); }}>Clear</button>
          )}
        </div>
        {txLoading ? (
          <div className="erp-loading" style={{ minHeight: 120 }}><RefreshCw className="spin" /> Loading transactions...</div>
        ) : txError ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#ef4444', fontSize: '.92rem' }}>{txError}</div>
        ) : txns.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: '.92rem' }}>No transactions found for this account.</div>
        ) : (
          <div className="erp-table-wrap" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>
              <thead>
                <tr>
                  {['Date', 'Type', 'Reference', 'Description', 'Money In', 'Money Out', 'By'].map(h => (
                    <th key={h} style={{ position: 'sticky', top: 0, background: '#fff', textAlign: 'left', color: '#94a3b8', fontSize: '.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txns.map((tx: any) => (
                  <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: '.82rem' }}>{date(tx.date)}</td>
                    <td style={{ padding: '10px 12px' }}><span className="erp-status" style={{ background: tx.debit > 0 ? '#d1fae5' : tx.credit > 0 ? '#fee2e2' : '#dbeafe', color: tx.debit > 0 ? '#065f46' : tx.credit > 0 ? '#991b1b' : '#1e40af', fontSize: '.72rem' }}>{tx.type}</span></td>
                    <td style={{ padding: '10px 12px', fontSize: '.82rem', color: '#64748b' }}>{tx.reference || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '.82rem' }}>{tx.counterparty || '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '.88rem', color: '#059669', fontWeight: 600 }}>{tx.debit > 0 ? money(tx.debit) : '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '.88rem', color: '#dc2626', fontWeight: 600 }}>{tx.credit > 0 ? money(tx.credit) : '-'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '.82rem', color: '#64748b' }}>{tx.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '10px 0', fontSize: '.88rem' }}>
            <button className="acct-btn acct-btn-secondary" style={{ padding: '6px 14px', fontSize: '.85rem' }} disabled={txPage <= 1} onClick={() => setTxPage(p => Math.max(1, p - 1))}>Prev</button>
            <span style={{ color: '#64748b' }}>Page {txPage} of {totalPages} ({txTotal} transactions)</span>
            <button className="acct-btn acct-btn-secondary" style={{ padding: '6px 14px', fontSize: '.85rem' }} disabled={txPage >= totalPages} onClick={() => setTxPage(p => Math.min(totalPages, p + 1))}>Next</button>
          </div>
        )}
      </div>, true);
  }

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />
    <div className="erp-heading">
      <div><h1>{titles[page]}</h1><p>{locationFilter ? `Filtered by ${locations.find(x => String(x.id) === String(locationFilter))?.name || 'selected location'}` : 'All permitted locations'}</p></div>
      <div className="erp-heading-actions">
        {page === 'cash-bank' && can('accounts.cash_bank.create') && (
          <div className="acct-header-btns">
            {[
              { icon: <Plus size={17} />, label: 'Add Account', bg: '#f1f5f9', color: '#0f172a', hover: '#475569', onClick: () => { setForm({ ...form, add_location: locationFilter || '' }); setModal('add-account'); } },
              { icon: <ArrowDownToLine size={17} />, label: 'Cash In', bg: '#d1fae5', color: '#059669', hover: '#10b981', onClick: () => setModal('cash-in') },
              { icon: <ArrowUpFromLine size={17} />, label: 'Cash Out', bg: '#fee2e2', color: '#dc2626', hover: '#ef4444', onClick: () => setModal('cash-out') },
              { icon: <ArrowRightLeft size={17} />, label: 'Transfer', bg: '#ede9fe', color: '#7c3aed', hover: '#8b5cf6', onClick: () => setModal('transfer') },
              { icon: <History size={17} />, label: 'History', bg: '#dbeafe', color: '#2563eb', hover: '#3b82f6', onClick: () => setModal('history') },
            ].map(b => (
              <button key={b.label} className="acct-header-btn" title={b.label} onClick={b.onClick}
                style={{ background: b.bg, color: b.color }}
                onMouseEnter={e => { e.currentTarget.style.background = b.hover; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = b.bg; e.currentTarget.style.color = b.color; }}>
                {b.icon}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* ── Modals ── */}
    {modal === 'cash-in' && modalContent('Cash In', modalForm(
      <>{fieldGroup('Account', <SearchableSelect value={form.account || ''} onChange={n => setForm({ ...form, account: n })} placeholder="Select" options={cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />)}
        {fieldGroup('Amount', <input className="acct-form-input" type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />)}
        {fieldGroup('Note', <input className="acct-form-input" value={form.note || ''} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Optional" />)}</>,
      'Record Cash In', () => saveMovement('CASH_IN'), 'acct-btn-success'))}
    {modal === 'cash-out' && modalContent('Cash Out', modalForm(
      <>{fieldGroup('Account', <SearchableSelect value={form.account || ''} onChange={n => setForm({ ...form, account: n })} placeholder="Select" options={cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />)}
        {fieldGroup('Amount', <input className="acct-form-input" type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />)}
        {fieldGroup('Note', <input className="acct-form-input" value={form.note || ''} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Optional" />)}</>,
      'Record Cash Out', () => saveMovement('CASH_OUT'), 'acct-btn-danger'))}
    {modal === 'transfer' && modalContent('Transfer Between Accounts', modalForm(
      <><div className="acct-form-row">
        {fieldGroup('From', <SearchableSelect value={form.source_account || ''} onChange={n => setForm({ ...form, source_account: n })} placeholder="Source" options={cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />)}
        {fieldGroup('To', <SearchableSelect value={form.destination_account || ''} onChange={n => setForm({ ...form, destination_account: n })} placeholder="Destination" options={cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />)}
      </div>
        {fieldGroup('Amount', <input className="acct-form-input" type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />)}
        {fieldGroup('Reference', <input className="acct-form-input" value={form.reference || ''} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Optional reference" />)}</>,
      'Create Transfer', createTransfer, 'acct-btn-primary'))}
    {modal === 'history' && modalContent('Transfer History',
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {(payload?.transfers || []).length === 0 ? <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>No transfers yet</div> :
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
            <thead><tr>{['#', 'From', 'To', 'Amount', 'Date', 'Status', ''].map(h => <th key={h} style={{ position: 'sticky', top: 0, background: '#fff', textAlign: 'left', color: '#94a3b8', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>{h}</th>)}</tr></thead>
            <tbody>{(payload?.transfers || []).map((x: any) => <tr key={x.id}>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 600, fontSize: '.72rem' }}>{x.transfer_number}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{x.source_account_name}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>{x.destination_account_name}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontWeight: 700 }}>{money(x.amount)}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: '.72rem' }}>{shortDate(x.date)}</td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}><span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: '.6rem', fontWeight: 700, background: x.status === 'COMPLETED' ? '#d1fae5' : x.status === 'CANCELLED' ? '#fee2e2' : x.status === 'DRAFT' ? '#fef3c7' : '#dbeafe', color: x.status === 'COMPLETED' ? '#065f46' : x.status === 'CANCELLED' ? '#991b1b' : x.status === 'DRAFT' ? '#92400e' : '#1e40af' }}>{x.status}</span></td>
              <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {x.status === 'DRAFT' && can('accounts.cash_transfer.approve') && <button onClick={() => { approveTransfer(x.id); }} style={{ border: 0, background: '#d1fae5', color: '#065f46', borderRadius: 5, padding: '4px 7px', cursor: 'pointer', fontWeight: 600, fontSize: '.6rem' }}>Approve</button>}
                  {(x.status === 'DRAFT' || x.status === 'APPROVED') && can('accounts.cash_transfer.create') && <button onClick={() => { completeTransfer(x.id); }} style={{ border: 0, background: '#dbeafe', color: '#1e40af', borderRadius: 5, padding: '4px 7px', cursor: 'pointer', fontWeight: 600, fontSize: '.6rem' }}>Complete</button>}
                  {(x.status === 'DRAFT' || x.status === 'APPROVED') && <button onClick={() => { cancelTransfer(x.id); }} style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 5, padding: '4px 7px', cursor: 'pointer', fontWeight: 600, fontSize: '.6rem' }}>Cancel</button>}
                </div>
              </td>
            </tr>)}</tbody>
          </table>}
      </div>, true)}
    {modal === 'add-account' && modalContent('Add Cash Account', modalForm(
      <><div className="acct-form-row">
        {fieldGroup('Chart of Account (Asset COA)', <SearchableSelect value={form.add_account || ''} onChange={n => setForm({ ...form, add_account: n })} placeholder="Select" options={coaAssetOptions} />)}
        {fieldGroup('Location', <select className="acct-form-input" value={form.add_location || ''} onChange={e => setForm({ ...form, add_location: e.target.value })}><option value="">— Shared —</option>{locations.filter((l: any) => l.is_active).map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>)}
      </div>
        {fieldGroup('Account Name', <input className="acct-form-input" value={form.add_name || ''} onChange={e => setForm({ ...form, add_name: e.target.value })} placeholder="e.g. Flagship Petty Cash" />)}
        {fieldGroup('Opening Balance', <input className="acct-form-input" type="number" value={form.add_balance || ''} onChange={e => setForm({ ...form, add_balance: e.target.value })} placeholder="0.00" />)}</>,
      'Create', createCashBankAccount, 'acct-btn-primary'))}
    {modal === 'edit-account' && modalContent('Edit Account', modalForm(
      <><div className="acct-form-row">
        {fieldGroup('Chart of Account', <SearchableSelect value={form.edit_account || ''} onChange={n => setForm({ ...form, edit_account: n })} placeholder="Select" options={coaAssetOptions} />)}
        {fieldGroup('Account No / ID', <input className="acct-form-input" value={form.edit_account_number || ''} onChange={e => setForm({ ...form, edit_account_number: e.target.value })} placeholder="Optional" />)}
      </div>
        {fieldGroup('Account Name', <input className="acct-form-input" value={form.edit_name || ''} onChange={e => setForm({ ...form, edit_name: e.target.value })} placeholder="Name" />)}
        {fieldGroup('Status', <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.85rem', fontWeight: 600, userSelect: 'none' }}>
          <div className={`acct-toggle ${form.edit_is_active !== false ? 'active' : 'inactive'}`} onClick={() => setForm({ ...form, edit_is_active: !form.edit_is_active })}>
            <div className="acct-toggle-knob" />
          </div>
          {form.edit_is_active !== false ? 'Active' : 'Inactive'}
        </label>)}
        {fieldGroup('Notes', <textarea className="acct-form-textarea" value={form.edit_description || ''} onChange={e => setForm({ ...form, edit_description: e.target.value })} placeholder="Optional" rows={3} />)}</>,
      'Save', updateCashBankAccount, 'acct-btn-primary'))}
    {modal === 'transaction-history' && <TransactionHistoryModal account={form.txn_history_account} />}

    {/* ── PAGE RENDERER ── */}
    {page === 'overview' && renderOverview()}
    {page === 'payables' && <PayablesPage me={me} locationFilter={locationFilter} />}
    {page === 'receivables' && <ReceivablesPage me={me} locationFilter={locationFilter} />}
    {page === 'transactions' && renderTransactions()}
    {page === 'ledger' && renderLedger()}
    {page === 'cash-bank' && renderCashBank()}
    {page === 'cash-sessions' && <SessionsView />}
    {page === 'cod-settlements' && <SettlementsView />}
    {page === 'expenses' && <ExpensesPage me={me} locationFilter={locationFilter} />}
    {page === 'journal-entries' && <JournalEntriesPage me={me} locationFilter={locationFilter} />}
    {page === 'financial-reports' && renderFinancialReports()}
    {page === 'settings' && renderSettings()}
    {page === 'profit-loss' && renderProfitLoss()}
  </div>;

function renderOverview() {
    const cards = payload?.cards || {};
    const navigate = (path: string) => window.open(path, '_self');
    const kpis = [
      { key: 'cash_balance', label: 'Cash Balance', color: '#059669', path: '/admin/accounts/cash-bank' },
      { key: 'bank_balance', label: 'Bank Balance', color: '#2563eb', path: '/admin/accounts/cash-bank' },
      { key: 'todays_collection', label: "Today's Collection", color: '#7c3aed', path: '/admin/accounts/cash-bank' },
      { key: 'receivable_due', label: 'Receivables', color: '#d97706', path: '/admin/accounts/receivables' },
      { key: 'payable_due', label: 'Payables', color: '#dc2626', path: '/admin/accounts/payables' },
      { key: 'inventory_value', label: 'Inventory', color: '#0891b2', path: '/admin/inventory' },
      { key: 'net_profit', label: 'Net Profit', color: '#059669', path: '/admin/accounts/financial-reports' },
      { key: 'total_expense', label: 'Expenses', color: '#ea580c', path: '/admin/accounts/expenses' },
    ];
    return <>
      <div className="acct-kpi-grid">
        {kpis.map(c => {
          const val = cards[c.key] ?? 0;
          return <div key={c.key} className="acct-kpi" style={{ cursor: 'pointer' }} onClick={() => navigate(c.path)} onMouseEnter={e => { e.currentTarget.style.borderColor = c.color; e.currentTarget.style.boxShadow = `0 4px 12px ${c.color}20`; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,.04)'; }}>
            <div className="acct-kpi-icon" style={{ background: `${c.color}12`, color: c.color }}>৳</div>
            <div><div className="acct-kpi-label">{c.label}</div><div className="acct-kpi-value" style={{ color: c.color }}>{money(val)}</div></div>
          </div>;
        })}
      </div>
      <DataTable title="Recent Transactions" cols={['Date', 'Ref', 'Type', 'Location', 'Method', 'Amount', 'By']} rows={(payload?.recent_transactions || []).map((x: any) => [date(x.date), x.reference, x.type, x.location, x.payment_method, money(x.amount), x.created_by])} />
      <DataTable title="Pending Payables" cols={['Bill', 'Type', 'Party', 'Total', 'Paid', 'Due', 'Status']} rows={(payload?.pending_payables || []).map((x: any) => [x.bill_no, x.type, x.party, money(x.total_amount), money(x.paid_amount), money(x.due_amount), x.status])} />
      <DataTable title="Pending Receivables" cols={['Invoice', 'Type', 'Party', 'Total', 'Received', 'Due', 'Status']} rows={(payload?.pending_receivables || []).map((x: any) => [x.invoice_no, x.type, x.party, money(x.total_amount), money(x.received_amount), money(x.due_amount), x.status])} />
      <DataTable title="Location-wise Profit" cols={['Location', 'Sales', 'Discount', 'COGS', 'Net Profit']} rows={(payload?.location_profit || []).map((x: any) => [x.location_name, money(x.sales_revenue), money(x.discount_expense), money(x.cost_of_goods_sold), money(x.net_profit)])} />
    </>;
  }



  function renderTransactions() {
    const list = payload?.results || [];
    const totalCount = payload?.count || list.length;
    const currentPage = payload?.page || 1;
    const pageSize = payload?.page_size || 50;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const navigateToSource = (refType: string, refId: string) => {
      const routes: Record<string, string> = {
        Sale: '/admin/sale-orders', Purchase: '/admin/purchases/history',
        EcommerceOrder: '/admin/orders', StockTransfer: '/admin/inventory/transfers',
        CODSettlement: '/admin/accounts/cash-bank', CashTransfer: '/admin/accounts/cash-bank',
        Expense: '/admin/accounts/expenses', ExpensePayment: '/admin/accounts/expenses',
        SupplierBillPayment: '/admin/accounts/payables', POSExchange: '/admin/pos-exchanges',
        ReceivableReceipt: '/admin/accounts/receivables',
      };
      const route = routes[refType];
      if (route) window.open(`${route}?id=${refId}`, '_blank');
    };
    return <>
      <div className="erp-filterbar">
        <input value={filters.transaction_type || ''} onChange={e => setFilters({ ...filters, transaction_type: e.target.value })} placeholder="Type" style={{ minWidth: 120 }} />
        <input value={filters.reference || ''} onChange={e => setFilters({ ...filters, reference: e.target.value })} placeholder="Reference" />
      </div>
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-wrap" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          <table style={{ position: 'relative' }}>
            <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
              <th>Date</th><th>Location</th><th>Account</th><th>Type</th><th>Counterparty</th><th>Money In</th><th>Money Out</th><th>Reference</th><th>By</th>
            </tr></thead>
            <tbody>{list.length === 0 ? <tr><td className="erp-empty" colSpan={9}>No transactions found.</td></tr> :
              list.map((x: any) => <tr key={x.id}
                onClick={() => { if (x.reference_type) navigateToSource(x.reference_type, x.reference_id); }}
                style={{ cursor: x.reference_type ? 'pointer' : 'default' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ whiteSpace: 'nowrap', fontSize: '.78rem' }}>{date(x.date)}</td>
                <td style={{ fontSize: '.78rem' }}>{x.location}</td>
                <td style={{ fontWeight: 500, fontSize: '.78rem' }}>{x.account}</td>
                <td><span className="erp-status" style={{ background: x.debit > 0 ? '#d1fae5' : '#fee2e2', color: x.debit > 0 ? '#065f46' : '#991b1b', fontSize: '.68rem' }}>{x.type}</span></td>
                <td style={{ fontSize: '.78rem', color: '#64748b' }}>{x.counterparty || '-'}</td>
                <td style={{ color: '#059669', fontWeight: 600 }}>{x.debit > 0 ? money(x.debit) : '-'}</td>
                <td style={{ color: '#dc2626', fontWeight: 600 }}>{x.credit > 0 ? money(x.credit) : '-'}</td>
                <td style={{ fontSize: '.75rem', color: '#64748b' }}>{x.reference}</td>
                <td style={{ fontSize: '.72rem', color: '#64748b' }}>{x.created_by}</td>
              </tr>)}</tbody>
          </table>
        </div>
        {totalPages > 1 && <div style={{ padding: '10px 14px', borderTop: '1px solid var(--admin-border)', display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center', fontSize: '.78rem' }}>
          <button className="erp-btn secondary" style={{ padding: '4px 8px', fontSize: '.7rem' }} disabled={currentPage <= 1}
            onClick={() => setFilters(f => ({ ...f, page: String(Math.max(1, currentPage - 1)) }))}>Prev</button>
          <span style={{ color: '#64748b' }}>Page {currentPage} of {totalPages}</span>
          <button className="erp-btn secondary" style={{ padding: '4px 8px', fontSize: '.7rem' }} disabled={currentPage >= totalPages}
            onClick={() => setFilters(f => ({ ...f, page: String(currentPage + 1) }))}>Next</button>
        </div>}
      </section>
    </>;
  }

  function renderLedger() {
    return <DataTable title="General Ledger" cols={['Date', 'Account', 'Location', 'Debit', 'Credit', 'Balance', 'Remarks']} rows={rows(payload).map((x: any) => [x.date, `${x.account_code} - ${x.account}`, x.location, money(x.debit), money(x.credit), money(x.running_balance), x.remarks])} />;
  }

  function renderProfitLoss() {
    const t = payload?.totals || {};
    const accounts = payload?.by_account || [];
    return <>
      <div className="acct-kpi-grid">
        {['sales_revenue', 'cost_of_goods_sold', 'discount_expense', 'other_expenses', 'net_profit'].map(k => (
          <div key={k} className="erp-stat"><span>{k.replaceAll('_', ' ')}</span><strong style={{ color: k === 'net_profit' ? (t[k] >= 0 ? '#059669' : '#dc2626') : k === 'sales_revenue' ? '#2563eb' : '#d97706' }}>{money(t[k])}</strong></div>
        ))}
      </div>
      {accounts.length > 0 && <DataTable title="General Ledger (Posted)" cols={['Account Code', 'Account Name', 'Type', 'Value']}
        rows={accounts.map((a: any) => [a.code, a.name, a.type, money(a.value)])} />}
    </>;
  }



  function renderFinancialReports() {
    const tabs = ['Profit & Loss', 'Balance Sheet', 'Trial Balance', 'Cash Flow', 'AR Aging', 'AP Aging'];

    const renderTable = (rows: any[], cols: { key: string; label: string; money?: boolean }[]) => {
      if (!rows?.length) return <div className="erp-empty" style={{ margin: 20 }}>No data.</div>;
      return <div className="erp-table-wrap"><table>
        <thead><tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r: any, i: number) => <tr key={i}>
          {cols.map(c => <td key={c.key}>{c.money !== false ? money(r[c.key]) : r[c.key]}</td>)}
        </tr>)}</tbody>
      </table></div>;
    };

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div className="acct-tabs" style={{ marginBottom: 0 }}>{tabs.map(t => <button key={t} className={`acct-tab ${reportTab === t ? 'active' : ''}`} onClick={() => setReportTab(t)}>{t}</button>)}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="date" lang="en-GB" value={frDates.start || ''} onChange={e => setFrDates(d => ({ ...d, start: e.target.value }))} style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
          <input type="date" lang="en-GB" value={frDates.end || ''} onChange={e => setFrDates(d => ({ ...d, end: e.target.value }))} style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
          <button className="erp-btn secondary" style={{ padding: '6px 10px', fontSize: '.75rem' }} onClick={() => {
            const dataStr = JSON.stringify(frData, null, 2);
            const blob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `${reportTab.replace(/\s/g, '_')}.json`; a.click();
            URL.revokeObjectURL(url);
          }}><Download size={14} /> Export</button>
        </div>
      </div>
      {frLoading ? <div className="erp-loading" style={{ minHeight: 200 }}><RefreshCw className="spin" /> Loading report...</div> :
        reportTab === 'Profit & Loss' && frData ? <>
          <div className="acct-kpi-grid">{['sales_revenue', 'discount_expense', 'cost_of_goods_sold', 'delivery_income', 'delivery_cost', 'other_expenses', 'net_profit'].map(k => <Stat key={k} label={k.replaceAll('_', ' ')} value={money(frData?.totals?.[k] || 0)} />)}</div>
          {renderTable(frData?.shop_wise || [], [{ key: 'location_name', label: 'Shop' }, { key: 'sales_revenue', label: 'Sales', money: true }, { key: 'discount_expense', label: 'Discount', money: true }, { key: 'cost_of_goods_sold', label: 'COGS', money: true }, { key: 'net_profit', label: 'Net', money: true }])}
        </> : reportTab === 'Trial Balance' && frData ? <>
          {renderTable(frData?.rows || [], [{ key: 'code', label: 'Code' }, { key: 'name', label: 'Account' }, { key: 'type', label: 'Type' }, { key: 'debit', label: 'Debit', money: true }, { key: 'credit', label: 'Credit', money: true }, { key: 'balance', label: 'Balance', money: true }])}
          <div style={{ padding: '0 14px 14px', fontWeight: 700, fontSize: '.85rem' }}>Total Debit: {money(frData?.totals?.debit)} | Total Credit: {money(frData?.totals?.credit)}</div>
        </> : reportTab === 'Balance Sheet' && frData ? <>
          <div className="acct-kpi-grid">
            <Stat label="Total Assets" value={money(frData?.assets?.total)} />
            <Stat label="Total Liabilities" value={money(frData?.liabilities?.total)} />
            <Stat label="Total Equity" value={money(frData?.equity?.total)} />
          </div>
          <DataTable title="Assets" cols={['Code', 'Account', 'Balance']} rows={(frData?.assets?.accounts || []).map((a: any) => [a.account__code, a.account__name, money(a.balance)])} />
        </> : reportTab === 'Cash Flow' && frData ? <>
          <div className="acct-kpi-grid">
            <Stat label="Inflow" value={frData?.inflow} />
            <Stat label="Outflow" value={frData?.outflow} />
            <Stat label="Net" value={frData?.net} />
          </div>
          {renderTable(frData?.by_type || [], [{ key: 'transaction_type', label: 'Type' }, { key: 'total_in', label: 'In', money: true }, { key: 'total_out', label: 'Out', money: true }])}
        </> : reportTab === 'AR Aging' && frData ? <>
          {renderTable(frData || [], [{ key: 'invoice', label: 'Invoice' }, { key: 'customer', label: 'Customer' }, { key: 'location', label: 'Location' }, { key: 'due', label: 'Due', money: true }, { key: 'age_days', label: 'Days' }, { key: 'bucket', label: 'Bucket' }])}
        </> : reportTab === 'AP Aging' && frData ? <>
          {renderTable(frData || [], [{ key: 'bill', label: 'Bill' }, { key: 'supplier', label: 'Supplier' }, { key: 'location', label: 'Location' }, { key: 'due', label: 'Due', money: true }, { key: 'age_days', label: 'Days' }, { key: 'bucket', label: 'Bucket' }])}
        </> : frData === null && !frLoading ? <div className="erp-card" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available for this report.</div> : null}
    </div>;
  }

  function renderSettings() {
    const pmts = payload?.paymentMethods || [];
    const cats = payload?.expenseCategories || [];

    const addCategory = async () => {
      if (!catForm.name) return;
      try { await erpAPI.create('expense-categories', { name: catForm.name, description: catForm.description || '' }); setCatForm({}); setMessage({ type: 'success', text: 'Category added.' }); load(); }
      catch (e: any) { setMessage({ type: 'error', text: errorText(e) }); }
    };
    const toggleCategory = async (id: number) => {
      try { await erpAPI.action('expense-categories', id, 'toggle', {}); load(); }
      catch (e: any) { setMessage({ type: 'error', text: errorText(e) }); }
    };
    const addPaymentAccount = async () => {
      if (!pmtForm.name || !pmtForm.code) return;
      try { await erpAPI.create('payment-accounts', { name: pmtForm.name, code: pmtForm.code.toUpperCase(), cash_bank_account: pmtForm.cash_bank_account ? Number(pmtForm.cash_bank_account) : null, is_active: true }); setPmtForm({}); setMessage({ type: 'success', text: 'Payment account added.' }); load(); }
      catch (e: any) { setMessage({ type: 'error', text: errorText(e) }); }
    };

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="acct-tabs">
        {[
          { key: 'payment-accounts', label: 'Payment Accounts' },
          { key: 'expense-categories', label: 'Expense Categories' },
          { key: 'fiscal-year', label: 'Fiscal Year' },
        ].map(t => (
          <button key={t.key} className={`acct-tab ${settingsTab === t.key ? 'active' : ''}`} onClick={() => setSettingsTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {settingsTab === 'payment-accounts' && <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-title"><h2>Payment Accounts</h2><span>{pmts.length} accounts</span></div>
        <div className="erp-table-wrap"><table>
          <thead><tr><th>Code</th><th>Name</th><th>Account</th><th>Status</th></tr></thead>
          <tbody>{pmts.length === 0 ? <tr><td className="erp-empty" colSpan={4}>No payment accounts configured.</td></tr> :
            pmts.map((p: any) => <tr key={p.id}>
              <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.code}</td>
              <td>{p.name}</td>
              <td style={{ fontSize: '.8rem' }}>{p.account_name || p.cash_bank_account_name || '-'}</td>
              <td><span className={`erp-status ${p.is_active ? 'received' : ''}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
            </tr>)}
            <tr>
              <td><input className="acct-form-input" value={pmtForm.code || ''} onChange={e => setPmtForm(f => ({ ...f, code: e.target.value }))} placeholder="CASH" style={{ width: 70, fontSize: '.78rem' }} /></td>
              <td><input className="acct-form-input" value={pmtForm.name || ''} onChange={e => setPmtForm(f => ({ ...f, name: e.target.value }))} placeholder="Cash" style={{ width: 140, fontSize: '.78rem' }} /></td>
              <td><select className="acct-form-input" value={pmtForm.cash_bank_account || ''} onChange={e => setPmtForm(f => ({ ...f, cash_bank_account: e.target.value }))} style={{ width: 150, fontSize: '.72rem' }}>
                <option value="">Link cash/bank account…</option>
                {(cashBankAccounts || []).map((a: any) => <option key={a.id} value={a.id}>{a.account || a.name} — {a.location_name}</option>)}
              </select></td>
              <td><button className="erp-btn" onClick={addPaymentAccount} style={{ padding: '4px 10px', fontSize: '.72rem' }}>+ Add</button></td>
            </tr>
          </tbody>
        </table></div>
      </section>}

      {settingsTab === 'expense-categories' && <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="erp-table-title"><h2>Expense Categories</h2><span>{cats.length} categories</span></div>
        <div className="erp-table-wrap"><table>
          <thead><tr><th>Name</th><th>Description</th><th>Status</th><th></th></tr></thead>
          <tbody>{cats.length === 0 ? <tr><td className="erp-empty" colSpan={4}>No categories defined. Add one below.</td></tr> :
            cats.map((c: any) => <tr key={c.id}>
              <td style={{ fontWeight: 600 }}>{c.name}</td>
              <td style={{ fontSize: '.8rem', color: '#64748b' }}>{c.description || '-'}</td>
              <td><span className={`erp-status ${c.is_active ? 'received' : ''}`}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
              <td>{can('accounts.settings') && <button onClick={() => toggleCategory(c.id)}
                style={{ border: 0, background: c.is_active ? '#fee2e2' : '#d1fae5', color: c.is_active ? '#991b1b' : '#065f46', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontWeight: 600, fontSize: '.65rem' }}>
                {c.is_active ? 'Deactivate' : 'Activate'}</button>}</td>
            </tr>)}
            <tr>
              <td><input className="acct-form-input" value={catForm.name || ''} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Rent" style={{ width: 140, fontSize: '.78rem' }} /></td>
              <td><input className="acct-form-input" value={catForm.description || ''} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" style={{ width: 200, fontSize: '.78rem' }} /></td>
              <td colSpan={2}><button className="erp-btn" onClick={addCategory} style={{ padding: '4px 10px', fontSize: '.72rem' }}>+ Add Category</button></td>
            </tr>
          </tbody>
        </table></div>
      </section>}

      {settingsTab === 'fiscal-year' && <div className="erp-card" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
        <Settings2 size={28} style={{ opacity: .3, marginBottom: 8 }} />
        <h3 style={{ margin: '0 0 4px', fontSize: '.95rem', color: '#64748b' }}>Fiscal Year Configuration</h3>
        <p style={{ margin: 0, fontSize: '.82rem' }}>Fiscal year start date, period locking, and year-end closing coming in a future update.</p>
      </div>}
    </div>;
  }

  function renderCashBank() {
    const accts = accounts?.length ? accounts : (payload?.accounts || []);
    const cbTabs = [
      { key: 'accounts', label: 'Accounts' },
      { key: 'cash-flow', label: 'Cash Flow' },
      { key: 'ledger', label: 'Ledger' },
      { key: 'transfers', label: 'Transfers' },
      { key: 'sessions', label: 'Sessions' },
      { key: 'settlements', label: 'Settlements' },
    ] as const;

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div className="acct-tabs">
        {cbTabs.map(t => (
          <button key={t.key} className={`acct-tab ${cashTab === t.key ? 'active' : ''}`} onClick={() => setCashTab(t.key)}>{t.label}</button>
        ))}
      </div>
      {cashTab === 'accounts' && <CashAccountsView accts={accts} can={can} setModal={setModal} setForm={setForm} coaAssetOptions={coaAssetOptions} />}
      {cashTab === 'cash-flow' && <CashFlowView params={params} />}
      {cashTab === 'ledger' && <CashBankLedgerView />}
      {cashTab === 'transfers' && <TransfersView payload={payload} can={can} setModal={setModal} setForm={setForm} accounts={accts} approveTransfer={approveTransfer} completeTransfer={completeTransfer} cancelTransfer={cancelTransfer} />}
      {cashTab === 'sessions' && <SessionsView />}
      {cashTab === 'settlements' && <SettlementsView />}
    </div>;
  }

  function CashAccountsView({ accts, can, setModal, setForm, coaAssetOptions }: any) {
    const totals = accts.reduce((acc: any, a: any) => {
      const t = (a.type || '').toUpperCase();
      if (t === 'CASH') acc.cash += a.current_balance || 0;
      else if (t === 'BANK') acc.bank += a.current_balance || 0;
      else acc.mobile += a.current_balance || 0;
      return acc;
    }, { cash: 0, bank: 0, mobile: 0 });

    const grouped: Record<string, any[]> = accts.reduce((g: Record<string, any[]>, a: any) => {
      const key = a.location_name || 'Shared';
      (g[key] = g[key] || []).push(a);
      return g;
    }, {} as Record<string, any[]>);

    const kpis = [
      { label: 'Cash', value: totals.cash, color: '#059669', bg: '#d1fae5', icon: <PiggyBank size={16} /> },
      { label: 'Bank', value: totals.bank, color: '#2563eb', bg: '#dbeafe', icon: <Landmark size={16} /> },
      { label: 'Mobile', value: totals.mobile, color: '#7c3aed', bg: '#ede9fe', icon: <Smartphone size={16} /> },
    ];

    const accountCard = (a: any) => {
      const m = typeMeta(a.type);
      const inactive = a.is_active === false;
      return (
        <div key={a.id} className={`acct-card${inactive ? ' inactive' : ''}`}
          onClick={() => { setForm({ txn_history_account: a }); setModal('transaction-history'); }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = m.color; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
          {inactive && <div className="acct-card-watermark">INACTIVE</div>}
          <div className="acct-card-header" style={{ background: `linear-gradient(135deg, ${m.bg} 0%, #fff 80%)` }}>
            <div className="acct-card-icon" style={{ color: m.color }}>{m.icon}</div>
            <div className="acct-card-info">
              <div className="acct-card-name">{a.account}</div>
              <div className="acct-card-coa">{a.account_name || m.label}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="acct-card-balance-label">Balance</div>
              <div className="acct-card-balance" style={{ color: a.current_balance >= 0 ? '#0f172a' : '#ef4444' }}>{money(a.current_balance)}</div>
            </div>
          </div>
          <div className="acct-card-body">
            <div className="acct-card-stat"><span className="acct-card-stat-label">Opened </span><strong className="acct-card-stat-value">{money(a.opening_balance)}</strong></div>
            <div className="acct-card-stat"><span style={{ color: '#10b981' }}>In </span><strong className="acct-card-stat-value">{money(a.inflow)}</strong></div>
            <div className="acct-card-stat"><span style={{ color: '#ef4444' }}>Out </span><strong className="acct-card-stat-value">{money(a.outflow)}</strong></div>
          </div>
          <div className="acct-card-footer">
            {can('accounts.cash_bank.create') && <button className="acct-card-edit" onClick={(e) => { e.stopPropagation(); setForm({ edit_id: a.id, edit_name: a.account, edit_account: a.account_id || '', edit_account_number: a.account_number || '', edit_description: a.description || '', edit_is_active: a.is_active !== false }); setModal('edit-account'); }} title="Edit"><Pencil size={12} /></button>}
            <button className="acct-card-delete" onClick={(e) => { e.stopPropagation(); deleteAccount(a.id); }} title="Delete"><X size={12} /></button>
          </div>
        </div>
      );
    };

    return <>
      <div className="acct-kpi-grid">
        {kpis.map(k => <div key={k.label} className="acct-kpi">
          <div className="acct-kpi-icon" style={{ background: k.bg, color: k.color }}>{k.icon}</div>
          <div><div className="acct-kpi-label">{k.label}</div><div className="acct-kpi-value" style={{ color: k.color }}>{money(k.value)}</div></div>
        </div>)}
      </div>
      {Object.entries(grouped).map(([locName, locAccts]) => {
        const expanded = expandedLocs[locName] !== false;
        const locTotal = locAccts.reduce((s: number, a: any) => s + (a.current_balance || 0), 0);
        return (
          <div key={locName} className="acct-loc-card">
            <div className={`acct-loc-header${expanded ? '' : ' not-expanded'}`} onClick={() => setExpandedLocs((prev: Record<string, boolean>) => ({ ...prev, [locName]: !prev[locName] }))}>
              <div className={`acct-loc-dot${expanded ? ' expanded' : ''}`} />
              <h3 className="acct-loc-name">{locName}</h3>
              <span className="acct-loc-count">{locAccts.length} accts</span>
              <span className="acct-loc-total" style={{ color: locTotal >= 0 ? '#0f172a' : '#ef4444' }}>{money(locTotal)}</span>
              <div className={`acct-loc-chevron${expanded ? ' expanded' : ''}`}>&#x25BE;</div>
            </div>
            {expanded && <div className="acct-loc-body">
              <div className="acct-card-grid">
                {[...locAccts].sort((a: any, b: any) => (a.is_active === false ? 1 : 0) - (b.is_active === false ? 1 : 0)).map(accountCard)}
              </div>
            </div>}
          </div>
        );
      })}
    </>;
  }

  function CashBankLedgerView() {
    const [entries, setEntries] = useState<any[]>([]);
    const [acctId, setAcctId] = useState('');
    const [ldgLoading, setLdgLoading] = useState(false);
    const [ldgInfo, setLdgInfo] = useState<Record<string, any>>({});
    const [ldgDates, setLdgDates] = useState<Record<string, string>>({});
    const accts = Array.isArray(payload?.accounts) ? payload.accounts : [];
    const acctOpts = accts.map((a: any) => ({ value: a.id, label: `${a.account} (${a.location_name})` }));

    useEffect(() => {
      if (!acctId) return;
      setLdgLoading(true);
      erpAPI.list('accounts-section/cash-bank-ledger', {
        account_id: acctId, ...(locationFilter ? { location: locationFilter } : {}),
        ...(ldgDates.start ? { start: ldgDates.start } : {}),
        ...(ldgDates.end ? { end: ldgDates.end } : {}),
      }).then(resp => {
        const d = resp.data;
        setLdgInfo({ account_name: d.account_name, opening: d.opening_balance, closing: d.closing_balance });
        setEntries(d.entries || []);
      }).catch(() => { setEntries([]); }).finally(() => setLdgLoading(false));
    }, [acctId, ldgDates, locationFilter]);

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="erp-filterbar" style={{ flexWrap: 'wrap' }}>
        <label>Account</label>
        <SearchableSelect value={acctId} onChange={setAcctId} placeholder="Select account" options={acctOpts} />
        <input type="date" lang="en-GB" value={ldgDates.start || ''} onChange={e => setLdgDates(d => ({ ...d, start: e.target.value }))} />
        <input type="date" lang="en-GB" value={ldgDates.end || ''} onChange={e => setLdgDates(d => ({ ...d, end: e.target.value }))} />
      </div>
      {ldgInfo.account_name && <div className="acct-kpi-grid">
        <div className="erp-stat"><span>Account</span><strong>{ldgInfo.account_name}</strong></div>
        <div className="erp-stat"><span>Opening</span><strong style={{ color: '#2563eb' }}>{money(ldgInfo.opening)}</strong></div>
        <div className="erp-stat"><span>Closing</span><strong style={{ color: '#059669' }}>{money(ldgInfo.closing)}</strong></div>
      </div>}
      {ldgLoading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading...</div> :
        <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="erp-table-wrap" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <table style={{ position: 'relative' }}>
              <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
                <th>Date</th><th>Module</th><th>Reference</th><th>Counterparty</th><th>Debit</th><th>Credit</th><th>Balance</th>
              </tr></thead>
              <tbody>{entries.length === 0 ? <tr><td className="erp-empty" colSpan={7}>Select an account to view ledger.</td></tr> :
                entries.map((e: any, i: number) => <tr key={e.id || i}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '.78rem' }}>{date(e.date)}</td>
                  <td><span className="erp-status">{e.module}</span></td>
                  <td style={{ fontSize: '.78rem', color: '#64748b' }}>{e.reference}</td>
                  <td style={{ fontSize: '.78rem' }}>{e.counterparty}</td>
                  <td style={{ color: '#059669', fontWeight: 600 }}>{e.debit > 0 ? money(e.debit) : '-'}</td>
                  <td style={{ color: '#dc2626', fontWeight: 600 }}>{e.credit > 0 ? money(e.credit) : '-'}</td>
                  <td style={{ fontWeight: 700 }}>{money(e.running_balance)}</td>
                </tr>)}</tbody>
            </table>
          </div>
        </section>}
    </div>;
  }

  function CashFlowView({ params }: { params: Record<string, any> }) {
    const [txns, setTxns] = useState<any[]>([]);
    const [cfLoading, setCfLoading] = useState(true);
    const [cfFilters, setCfFilters] = useState<Record<string, any>>({});
    const [cfPage, setCfPage] = useState(1);
    const [cfTotal, setCfTotal] = useState(0);
    const [cfSummary, setCfSummary] = useState<Record<string, number>>({});
    const limit = 25;

    const setQuickDate = (label: string) => {
      const today = new Date();
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      if (label === 'Today') setCfFilters(f => ({ ...f, start: fmt(today), end: fmt(today) }));
      else if (label === 'This Week') {
        const mon = new Date(today); mon.setDate(today.getDate() - (today.getDay() || 7) + 1);
        setCfFilters(f => ({ ...f, start: fmt(mon), end: fmt(today) }));
      } else if (label === 'This Month') {
        setCfFilters(f => ({ ...f, start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today) }));
      } else if (label === 'Clear') {
        const { start, end, ...rest } = cfFilters;
        setCfFilters(rest);
      }
    };

    const cfParams = useMemo(() => ({ ...params, ...cfFilters, page: cfPage, page_size: limit }), [params, cfFilters, cfPage]);

    useEffect(() => {
      setCfLoading(true);
      erpAPI.list('accounts-section/transactions', cfParams)
        .then(resp => {
          const d = resp.data;
          const results = d.results || [];
          setTxns(results);
          setCfTotal(d.count || 0);
          const sum: Record<string, number> = { inflow: 0, outflow: 0 };
          results.forEach((x: any) => {
            if (x.debit > 0) sum.inflow += Number(x.debit);
            if (x.credit > 0) sum.outflow += Number(x.credit);
          });
          setCfSummary(sum);
        })
        .catch(() => { setTxns([]); setCfSummary({}); })
        .finally(() => setCfLoading(false));
    }, [cfParams]);

    const totalPages = Math.ceil(cfTotal / limit) || 1;
    const closingBalance = cfSummary.inflow - cfSummary.outflow;
    const quickBtns = ['Today', 'This Week', 'This Month'];

    function openSourceDocument(x: any) {
      const refType = x.reference_type || '';
      const refId = x.reference_id || x.reference;
      if (!refId) return;
      if (refType === 'Sale' || refType === 'POS Sale') window.open(`/admin/sale-orders?id=${refId}`, '_blank');
      else if (refType === 'SupplierBillPayment') window.open(`/admin/purchases/history?id=${refId}`, '_blank');
      else if (refType === 'CashTransfer') window.open(`/admin/accounts/cash-bank`, '_blank');
      else if (refType === 'CODSettlement') window.open(`/admin/accounts/cash-bank`, '_blank');
      else if (refType === 'ReceivableReceipt') window.open(`/admin/accounts/receivables`, '_blank');
      else if (refType === 'Purchase') window.open(`/admin/purchases/history?id=${refId}`, '_blank');
    }

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Summary Cards */}
      <div className="acct-kpi-grid">
        <Stat label="Opening Balance" value={money(0)} />
        <Stat label="Total Inflow" value={money(cfSummary.inflow)} />
        <Stat label="Total Outflow" value={money(cfSummary.outflow)} />
        <Stat label="Net Movement" value={money(closingBalance)} />
      </div>
      {/* Quick Filters */}
      <div className="erp-filterbar" style={{ flexWrap: 'wrap' }}>
        {quickBtns.map(b => (
          <button key={b} onClick={() => setQuickDate(b)}
            style={{ border: 0, background: cfFilters.start === new Date().toISOString().slice(0, 10) && b === 'Today' ? '#2563eb' : '#f1f5f9', color: cfFilters.start === new Date().toISOString().slice(0, 10) && b === 'Today' ? '#fff' : '#475569', borderRadius: 7, padding: '6px 12px', fontWeight: 600, fontSize: '.75rem', cursor: 'pointer' }}>{b}</button>
        ))}
        {(cfFilters.start || cfFilters.end) && <button onClick={() => setQuickDate('Clear')} style={{ border: 0, background: '#fee2e2', color: '#991b1b', borderRadius: 7, padding: '6px 12px', fontWeight: 600, fontSize: '.75rem', cursor: 'pointer' }}>Clear</button>}
        <input type="date" lang="en-GB" value={cfFilters.start || ''} onChange={e => setCfFilters(f => ({ ...f, start: e.target.value }))} style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
        <input type="date" lang="en-GB" value={cfFilters.end || ''} onChange={e => setCfFilters(f => ({ ...f, end: e.target.value }))} style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
        <input value={cfFilters.transaction_type || ''} onChange={e => setCfFilters(f => ({ ...f, transaction_type: e.target.value }))} placeholder="Type" style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
        <input value={cfFilters.payment_method || ''} onChange={e => setCfFilters(f => ({ ...f, payment_method: e.target.value }))} placeholder="Method" style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
        <input value={cfFilters.reference || ''} onChange={e => setCfFilters(f => ({ ...f, reference: e.target.value }))} placeholder="Reference" style={{ minHeight: 34, border: '1px solid #cbd5e1', borderRadius: 7, padding: '4px 10px', fontSize: '.8rem' }} />
      </div>
      {/* Table */}
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        {cfLoading ? <div className="erp-loading" style={{ minHeight: 150 }}><RefreshCw className="spin" /> Loading...</div> :
          <div className="erp-table-wrap"><table>
            <thead><tr><th>Date</th><th>Location</th><th>Account</th><th>Module</th><th>Reference</th><th>Counterparty</th><th>In</th><th>Out</th><th>Balance</th><th>By</th></tr></thead>
            <tbody>{txns.length === 0 ? <tr><td className="erp-empty" colSpan={10}>No transactions.</td></tr> :
              txns.map((x: any, i: number) => <tr key={x.id || i}
                onClick={() => openSourceDocument(x)}
                style={{ cursor: x.reference ? 'pointer' : 'default', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ whiteSpace: 'nowrap' }}>{date(x.date)}</td>
                <td>{x.location}</td>
                <td style={{ fontWeight: 500 }}>{x.account_name || '-'}</td>
                <td><span className="erp-status">{x.type || x.transaction_type || '-'}</span></td>
                <td style={{ fontWeight: 600 }}>{x.reference || '-'}</td>
                <td>{x.counterparty || x.note || '-'}</td>
                <td style={{ color: '#10b981', fontWeight: 600 }}>{x.debit > 0 ? money(x.debit) : '-'}</td>
                <td style={{ color: '#ef4444', fontWeight: 600 }}>{x.credit > 0 ? money(x.credit) : '-'}</td>
                <td style={{ fontWeight: 700 }}>{money(closingBalance)}</td>
                <td style={{ fontSize: '.75rem', color: '#64748b' }}>{x.created_by || '-'}</td>
              </tr>)}</tbody>
          </table></div>}
      </section>
      {totalPages > 1 && <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', fontSize: '.82rem' }}>
        <button className="erp-btn secondary" style={{ padding: '5px 10px' }} disabled={cfPage <= 1} onClick={() => setCfPage(p => Math.max(1, p - 1))}>Prev</button>
        <span style={{ color: '#64748b' }}>Page {cfPage} of {totalPages}</span>
        <button className="erp-btn secondary" style={{ padding: '5px 10px' }} disabled={cfPage >= totalPages} onClick={() => setCfPage(p => p + 1)}>Next</button>
      </div>}
    </div>;
  }

  function TransfersView({ payload, can, setModal, setForm, accounts, approveTransfer, completeTransfer, cancelTransfer }: any) {
    const list = Array.isArray(payload?.transfers) ? payload.transfers : payload && Array.isArray(payload) ? payload : [];
    return (
      <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
        {can('accounts.cash_transfer.create') && (
          <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <div className="acct-form-row">
              <SearchableSelect value={form.source_account || ''} onChange={n => setForm({ ...form, source_account: n })} placeholder="From Account" options={accounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />
              <SearchableSelect value={form.destination_account || ''} onChange={n => setForm({ ...form, destination_account: n })} placeholder="To Account" options={accounts.map((x: any) => ({ value: x.id, label: `${x.account} - ${x.location_name}` }))} />
            </div>
            <div className="acct-form-row">
              <input className="acct-form-input" type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="Amount" />
              <input className="acct-form-input" value={form.reference || ''} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Reference" />
            </div>
            <button className="erp-btn" onClick={() => createTransfer()} style={{ marginTop: 8 }}><ArrowRightLeft size={15} /> Create Transfer</button>
          </div>
        )}
        <div className="erp-table-wrap"><table>
          <thead><tr><th>#</th><th>From</th><th>To</th><th>Amount</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>{list.length === 0 ? <tr><td className="erp-empty" colSpan={7}>No transfers</td></tr> :
            list.map((x: any) => <tr key={x.id}>
              <td style={{ fontWeight: 600 }}>{x.transfer_number}</td><td>{x.source_account_name}</td><td>{x.destination_account_name}</td>
              <td style={{ fontWeight: 700 }}>{money(x.amount)}</td><td>{shortDate(x.date)}</td>
              <td><span className={`erp-status ${x.status === 'COMPLETED' ? 'received' : x.status === 'CANCELLED' ? 'cancelled' : ''}`} style={{ background: x.status === 'DRAFT' ? '#fef3c7' : x.status === 'APPROVED' ? '#dbeafe' : undefined, color: x.status === 'DRAFT' ? '#92400e' : x.status === 'APPROVED' ? '#1e40af' : undefined }}>{x.status}</span></td>
              <td><div className="erp-actions">
                {x.status === 'DRAFT' && can('accounts.cash_transfer.approve') && <button className="erp-btn" style={{ background: '#d1fae5', color: '#065f46', padding: '4px 8px', fontSize: '.7rem' }} onClick={() => approveTransfer(x.id)}>Approve</button>}
                {(x.status === 'DRAFT' || x.status === 'APPROVED') && can('accounts.cash_transfer.create') && <button className="erp-btn" style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 8px', fontSize: '.7rem' }} onClick={() => completeTransfer(x.id)}>Complete</button>}
                {(x.status === 'DRAFT' || x.status === 'APPROVED') && <button className="erp-btn danger" style={{ padding: '4px 8px', fontSize: '.7rem' }} onClick={() => cancelTransfer(x.id)}>Cancel</button>}
              </div></td>
            </tr>)}</tbody>
        </table></div>
      </section>
    );
  }

  function SessionsView() {
    const list = Array.isArray(payload) ? payload : [];
    const cashOpts = cashBankAccounts.filter((a: any) => (a.type || a.account_type) === 'CASH').map((x: any) => ({ value: x.id, label: `${x.account || x.name} - ${x.location_name}` }));
    return <>
      {can('accounts.cash_session.open') && <section className="erp-card"><div className="erp-table-title"><h2>Open Session</h2></div><div className="account-action-row">
        <SearchableSelect value={form.cash_account || ''} onChange={n => setForm({ ...form, cash_account: n })} placeholder="Cash Account" options={cashOpts} />
        <input className="account-inline-input" type="number" value={form.opening_float || ''} onChange={e => setForm({ ...form, opening_float: e.target.value })} placeholder="Float" />
        <input className="account-inline-input" value={form.session_notes || ''} onChange={e => setForm({ ...form, session_notes: e.target.value })} placeholder="Notes" />
        <button className="erp-btn" onClick={openSession}><Plus size={15} />Open</button>
      </div></section>}
      <DataTable title="Sessions" cols={['Location', 'Account', 'Cashier', 'Float', 'Cash Sales', 'Card', 'Mobile', 'COD', 'Refunds', 'C.In', 'C.Out', 'Expected', 'Counted', 'Diff', 'Status', 'Opened', '']}
        rows={list.map((x: any) => [x.location_name, x.cash_account_name, x.cashier_name, money(x.opening_float), money(x.cash_sales), money(x.card_sales), money(x.mobile_sales), money(x.cod_collections), money(x.refunds), money(x.cash_in), money(x.cash_out), money(x.expected_closing), money(x.counted_cash), money(x.cash_difference), x.status, shortDate(x.opening_time),
          x.status === 'OPEN' ? <div className="erp-actions"><input className="account-inline-input" type="number" placeholder="Counted" value={form[`counted_${x.id}`] || ''} onChange={e => setForm({ ...form, [`counted_${x.id}`]: e.target.value })} /><input className="account-inline-input" type="text" placeholder="Notes" value={form[`close_notes_${x.id}`] || ''} onChange={e => setForm({ ...form, [`close_notes_${x.id}`]: e.target.value })} style={{ width: 70 }} />{can('accounts.cash_session.close') && <button className="erp-btn" onClick={() => closeSession(x.id)}>Close</button>}</div> : x.status === 'CLOSED' && can('accounts.cash_session.reconcile') ? <button className="erp-btn" onClick={() => reconcileSession(x.id)}>Reconcile</button> : '-'
        ])} />
    </>;
  }

  function SettlementsView() {
    const list = Array.isArray(payload) ? payload : [];
    const opts = cashBankAccounts.map((x: any) => ({ value: x.id, label: `${x.account || x.name} - ${x.location_name}` }));
    const amount = Number(form.settlement_amount || 0);
    const selectedIds: number[] = Array.isArray(form.settlement_order_ids)
      ? form.settlement_order_ids.map(Number).filter(Boolean) : [];
    const selectedRec = receivables.filter((r: any) => selectedIds.includes(Number(r.id)));
    let allocated = 0;
    let remaining = amount;
    const allocBy: Record<number, number> = {};
    for (const r of selectedRec) {
      const a = Math.min(Number(r.due_amount || 0), remaining);
      allocBy[Number(r.id)] = a;
      allocated += a;
      remaining -= a;
    }
    const toggleRow = (id: number) => setForm({
      ...form,
      settlement_order_ids: selectedIds.includes(id)
        ? selectedIds.filter((x: number) => x !== id)
        : [...selectedIds, id],
    });
    const canSubmit = !!form.settlement_provider && amount > 0 && !!form.settlement_account
      && selectedRec.length > 0 && allocated > 0;
    return <>
      {can('accounts.cod_settlement.create') && <section className="erp-card">
        <div className="erp-table-title"><h2>New Settlement</h2></div>
        <div className="acct-form-row">
          <SearchableSelect
            value={form.settlement_provider || ''}
            onChange={n => setForm({ ...form, settlement_provider: n })}
            placeholder="Shipping Provider (required)"
            options={shippingProviders.map((p: any) => ({ value: p.id, label: `${p.name} (${p.code})${p.is_active ? '' : ' — inactive'}` }))}
          />
          <SearchableSelect value={form.settlement_account || ''} onChange={n => setForm({ ...form, settlement_account: n })} placeholder="Deposit To (Cash/Bank required)" options={opts} />
          <input className="acct-form-input" type="number" value={form.settlement_amount || ''} onChange={e => setForm({ ...form, settlement_amount: e.target.value })} placeholder="Settlement Amount" />
          <input className="acct-form-input" type="date" lang="en-GB" value={form.settlement_date || ''} onChange={e => setForm({ ...form, settlement_date: e.target.value })} />
        </div>
        <div className="acct-form-row">
          <input className="acct-form-input" value={form.settlement_ref || ''} onChange={e => setForm({ ...form, settlement_ref: e.target.value })} placeholder="Reference No" />
          <input className="acct-form-input" value={form.settlement_notes || ''} onChange={e => setForm({ ...form, settlement_notes: e.target.value })} placeholder="Notes" />
        </div>
        <div style={{ fontWeight: 600, fontSize: '.78rem', margin: '10px 0 6px' }}>Allocate Courier Receivables</div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto', maxHeight: 200, marginBottom: 8 }}>
          {receivables.length === 0 ? (
            <div style={{ padding: 14, fontSize: '.75rem', color: '#94a3b8' }}>No unpaid COD receivables.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
              <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: '5px 8px' }}>Apply</th>
                <th style={{ padding: '5px 8px' }}>Invoice</th>
                <th style={{ padding: '5px 8px' }}>Total</th>
                <th style={{ padding: '5px 8px' }}>Outstanding</th>
                <th style={{ padding: '5px 8px' }}>Allocated</th>
              </tr></thead>
              <tbody>
                {receivables.map((r: any) => {
                  const on = selectedIds.includes(Number(r.id));
                  return <tr key={r.id}>
                    <td style={{ padding: '5px 8px' }}><input type="checkbox" checked={on} onChange={() => toggleRow(Number(r.id))} /></td>
                    <td style={{ padding: '5px 8px' }}>{r.invoice_number || r.invoice || '-'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{money(r.total_amount)}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#b45309' }}>{money(r.due_amount)}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{on ? money(allocBy[Number(r.id)] || 0) : money(0)}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '.75rem', marginBottom: 10 }}>
          <span>Settlement: <strong>{money(amount)}</strong></span>
          <span>Allocated: <strong style={{ color: '#059669' }}>{money(allocated)}</strong></span>
          <span>Unallocated: <strong style={{ color: allocated < amount ? '#b91c1c' : '#475569' }}>{money(Math.max(amount - allocated, 0))}</strong></span>
          {selectedRec.length > 0 && allocated < amount && <span style={{ color: '#b45309' }}>Remaining stays unapplied (not booked to receivables).</span>}
        </div>
        <button className="erp-btn" disabled={!canSubmit} onClick={createCODSettlement} style={{ marginTop: 4, opacity: canSubmit ? 1 : .5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
          <Truck size={15} /> Record Settlement
        </button>
      </section>}
      <DataTable title="Settlements"
        cols={['Number', 'Courier', 'Account', 'Amount', 'Allocated', 'Unallocated', 'Date', 'Reference', 'Status', 'By', 'Orders']}
        rows={list.map((x: any) => {
          const alloc = (x.orders || []).reduce((s: number, o: any) => s + Number(o.amount || 0), 0);
          return [x.settlement_number, x.shipping_provider_name || x.courier || '—', x.account_name, money(x.amount), money(alloc),
            money(Math.max(Number(x.amount) - alloc, 0)), shortDate(x.settlement_date),
            x.reference_number || '-', x.status, x.created_by_name, (x.orders || []).length];
        })} />
    </>;
  }

};

const Stat = ({ label, value }: { label: string; value: any }) => <div className="erp-stat"><span>{label}</span><strong>{value}</strong></div>;

const DataTable = ({ title, cols, rows }: { title: string; cols: string[]; rows: any[][] }) => (
  <section className="erp-card"><div className="erp-table-title"><h2>{title}</h2><span>{rows.length} records</span></div><div className="erp-table-wrap"><table><thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead><tbody>{rows.length ? rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>) : <tr><td className="erp-empty" colSpan={cols.length}>No records.</td></tr>}</tbody></table></div></section>
);

export default AccountsManagement;